import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { ReactNode, ElementType, MouseEventHandler } from 'react'

interface MobileListItemProps {
  /** Icon component (rendered inside the tinted tile) — or pass `iconNode` for a fully custom node like an Avatar. */
  icon?: ElementType
  iconNode?: ReactNode
  /** Override the tile background. Defaults to brand-ice / brand-royal/15 dark. */
  iconTileClassName?: string
  /** Override the icon color. Defaults to brand-royal. */
  iconClassName?: string

  title: ReactNode
  subtitle?: ReactNode
  /** Tiny line below subtitle for timestamps etc. */
  meta?: ReactNode

  /** Right-aligned slot for status pills / badges. */
  status?: ReactNode
  /** Right-side actions slot (e.g. <DropdownMenu>). Pushed to the far right after `status`. */
  actions?: ReactNode

  /** If set, the row is a Link. Renders a chevron on the right. */
  to?: string
  /** If set, the row is a button. Renders a chevron on the right. */
  onClick?: MouseEventHandler<HTMLDivElement>

  /** Optional extra content rendered below the header row, full width. */
  children?: ReactNode

  className?: string
}

/**
 * Standardized mobile list row used across Calls / Leads / Team /
 * Follow-Up Queue / etc. Mirrors the Scorpion concept: tinted icon tile,
 * title + subtitle, right-aligned status pill, optional chevron when tappable.
 *
 * Pass `children` to render extra content below the header row (badges,
 * progress bars, summaries) without breaking the row's anatomy.
 */
export function MobileListItem({
  icon: Icon,
  iconNode,
  iconTileClassName,
  iconClassName,
  title,
  subtitle,
  meta,
  status,
  actions,
  to,
  onClick,
  children,
  className,
}: MobileListItemProps) {
  const isInteractive = Boolean(to || onClick)
  const showChevron = isInteractive && !actions

  const inner = (
    <div className={cn('flex items-start gap-3 p-3 w-full text-left', className)}>
      {(Icon || iconNode) && (
        <div
          className={cn(
            'h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0',
            !iconTileClassName && 'bg-brand-ice dark:bg-brand-royal/15',
            iconTileClassName
          )}
        >
          {iconNode
            ? iconNode
            : Icon
            ? <Icon className={cn('h-5 w-5', !iconClassName && 'text-brand-royal', iconClassName)} />
            : null}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate leading-tight">{title}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground truncate mt-0.5 leading-tight">
                {subtitle}
              </p>
            )}
            {meta && (
              <p className="text-[11px] text-muted-foreground/70 truncate mt-1 leading-tight">
                {meta}
              </p>
            )}
          </div>
          {status && (
            <div className="flex-shrink-0 flex items-center">
              {status}
            </div>
          )}
        </div>
        {children && <div className="mt-2">{children}</div>}
      </div>

      {actions && (
        <div className="flex-shrink-0 -mt-1 -mr-1">
          {actions}
        </div>
      )}
      {showChevron && (
        <ChevronRight className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 mt-2.5" />
      )}
    </div>
  )

  if (to) {
    return (
      <Link
        to={to}
        className="block hover:bg-accent/40 active:bg-accent/60 transition-colors"
      >
        {inner}
      </Link>
    )
  }

  if (onClick) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement).click()
          }
        }}
        className="block hover:bg-accent/40 active:bg-accent/60 transition-colors cursor-pointer"
      >
        {inner}
      </div>
    )
  }

  return inner
}
